<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Poem freedom ko sirf political slogan nahi balki human dignity, courage aur rightful living ke ideal ke tor par present karti hai.",
  "Is unit ka central concern yeh hai ke real freedom delay, fear ya empty promise se hasil nahi hoti; us ke liye awareness aur moral strength chahiye.",
  "Exam prep me central idea, tone, message aur freedom ke practical meaning ko explain karna important hota hai.",
];

const chapterCoverage = [
  "Central idea of freedom",
  "Freedom and dignity",
  "Delay and urgency",
  "Tone and message",
  "Human response and moral seriousness",
];

const quickRevisionPoints = [
  "Poem freedom ko basic human need ke tor par treat karti hai.",
  "Freedom dignity aur meaningful life se linked hai.",
  "Delayed freedom poem ke mutabiq weak idea hai.",
  "Tone urgent, serious aur assertive hai.",
  "Message passive waiting ke against hai.",
];

const readingQuestions = [
  {
    question: "What is the central idea of the poem Freedom?",
    answer: "Central idea yeh hai ke freedom insani life ki basic zarurat hai aur is ko postpone ya ignore nahi kiya ja sakta. Poem dignity, action aur meaningful life ko freedom ke sath connect karti hai.",
  },
  {
    question: "How is freedom presented in the poem?",
    answer: "Freedom ko ek urgent, living need ke tor par present kiya gaya hai, na ke sirf idealistic dream ke tor par. Poem is baat par focus deti hai ke freedom ko real life me feel aur secure kiya jana chahiye.",
  },
  {
    question: "Why does the poem reject delay in gaining freedom?",
    answer: "Poem ke mutabiq delayed freedom apni real value kho deti hai, kyun ke insaan ko respect aur justice zinda rehte hue chahiye hoti hai. Is liye wait karne ka attitude weak aur unacceptable dikhaya gaya hai.",
  },
  {
    question: "What message does the poem give about human dignity?",
    answer: "Poem ka message hai ke dignity aur freedom ek doosre se jude hue hain. Agar freedom nahi ho to insaan ki selfhood aur confidence bhi affect hote hain.",
  },
  {
    question: "What kind of response does the poem demand from the reader?",
    answer: "Poem passive attitude ke bajaye alert, serious aur morally awake response demand karti hai. Reader ko feel hota hai ke freedom ke issue ko lightly nahi lena chahiye.",
  },
];

const literaryPoints = [
  {
    label: "Tone",
    detail: "Tone earnest, urgent aur assertive hai. Poem me emotional force hai jo reader ko immediate importance ka ehsaas dilati hai.",
  },
  {
    label: "Theme",
    detail: "Major themes me freedom, dignity, resistance to delay, aur human self-respect shamil hain.",
  },
  {
    label: "Message",
    detail: "Poem ka message yeh hai ke freedom ek living right hai, future promise nahi. Is ko meaningful human life ke liye ab aur yahin chahiye.",
  },
  {
    label: "Effect",
    detail: "Poem reader me seriousness aur moral urgency paida karti hai. Yeh sirf appreciation nahi balki inward awakening ka effect create karti hai.",
  },
];

const mcqs = [
  {
    question: "How is freedom mainly presented in the poem?",
    options: [
      { label: "a", text: "as a distant luxury with no urgency", verdict: "incorrect", reason: "Poem freedom ko urgent human need ke tor par present karti hai, luxury ke tor par nahi." },
      { label: "b", text: "as a basic condition for dignity and meaningful life", verdict: "correct", reason: "Poem ka central link freedom aur human dignity ke darmiyan hi hai." },
      { label: "c", text: "as a reward only for a few special people", verdict: "incorrect", reason: "Poem freedom ko broader human right ke tor par treat karti hai." },
      { label: "d", text: "as a matter that can be delayed without loss", verdict: "incorrect", reason: "Delay ko poem reject karti hai because delayed freedom apni force kho deti hai." },
    ],
  },
  {
    question: "The tone of the poem Freedom is best described as:",
    options: [
      { label: "a", text: "playful and humorous", verdict: "incorrect", reason: "Poem ka subject serious moral concern hai, comedy nahi." },
      { label: "b", text: "urgent and assertive", verdict: "correct", reason: "Poem reader ko immediate seriousness aur moral awakening ka feel deti hai." },
      { label: "c", text: "careless and indifferent", verdict: "incorrect", reason: "Indifference poem ke message ke bilkul opposite hai." },
      { label: "d", text: "purely nostalgic", verdict: "incorrect", reason: "Focus memory ya past se zyada present need aur demand par hai." },
    ],
  },
];

const answerBuilding = [
  "Central idea wale answer me freedom ko dignity aur real life ke sath connect karo.",
  "Tone question me urgent, assertive aur serious words use karna behtar hota hai.",
  "Message me delay, passivity aur meaningful human existence ke points include karo.",
  "Agar question urgency par ho to `living right, not future promise` type idea likho.",
];

usePageSeo({
  title: "Freedom Notes | 11th Class English Unit 9",
  description: "11th class English Unit 9 poem notes with central idea, tone, message, dignity, liberty and explained MCQs.",
  keywords: "freedom poem notes, 11th class english unit 9, class 11 freedom poem, freedom summary and explanation, freedom mcqs",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Poems", item: "/11th/english/poems/" },
    { name: "Freedom", item: "/11th/english/poems/freedom/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 9"
    title="Freedom"
    intro="Yeh page poem ke freedom-as-dignity message ko exam prep format me explain karta hai. Is unit ka stress immediate human value aur moral seriousness par hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 9 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Explanation Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Literary Points', items: literaryPoints.map((item) => ({ title: item.label, description: item.detail })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





